import { format } from 'date-fns/format';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { SCButton, SCLoader, SCPagination } from 'rollup-allspark';
import HelpPopover from '../../../components/HelpPopover';
import {
  DEFAULT_PAGE_NO,
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZES,
} from '../../../config';
import { isEmpty } from '../../../utils';
import { formatDateExact } from '../../../utils/format-date';
import { useInjectReducer } from '../../../utils/injectReducer';
import { useInjectSaga } from '../../../utils/injectSaga';
import {
  generateBrandWiseBusyLogData,
  generatePauseStoreLogCSVData,
  generateXLSXDataFromCSVData,
  sortAggregatedDataRow,
} from '../../../utils/nativeAnalyticsUtils';
import { convertToHoursAndMinutes } from '../../../utils/numToTime';
import {
  downloadCsvFromJSON,
  downloadXLSXFromJSON,
} from '../../../utils/sheetUtils';
import SectionCard from '../components/SectionCard';
import reducer from '../reducer';
import saga from '../saga';
import * as selectors from '../selectors';

const BusyModeLogTable = ({
  busyModeLogData,
  busyModeLogDataLoading,
  selectedBranch,
  brands,
}) => {
  useInjectReducer({ key: 'nativeAnalytics', reducer });
  useInjectSaga({ key: 'nativeAnalytics', saga });

  // CONSTANTS

  const headerKeys = [
    {
      title: 'Start time',
      key: 'start_time',
      sortable: true,
    },
    {
      title: 'End time',
      key: 'end_time',
      sortable: true,
    },
    {
      title: 'Brand',
      key: 'brand_id',
    },
    {
      title: 'Branch',
      key: 'branch_id',
    },
    {
      title: 'Busy min',
      key: 'busy_mins',
    },
    {
      title: 'Reason',
      key: 'busy_mode_reason',
      sortable: true,
    },
  ];

  // HOOKS
  const [tableRowData, setTableRowData] = useState([]);
  const [isHeaderPopoverOpen, setIsHeaderPopoverOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE_NO);
  const [currentSize, setCurrentSize] = useState(DEFAULT_PAGE_SIZE);
  const [paginatedTableRowData, setPaginatedTableRowData] = useState([]);

  useEffect(() => {
    setSortOrder('desc');
    setSortBy('start_time');
    setCurrentPage(DEFAULT_PAGE_NO);
  }, []);

  useEffect(() => {
    const data =
      busyModeLogData && busyModeLogData.logs && busyModeLogData.logs.length
        ? busyModeLogData.logs
        : [];
    const brandWiseBusyLogData = generateBrandWiseBusyLogData(data);
    setTableRowData(
      sortAggregatedDataRow(brandWiseBusyLogData, sortBy, sortOrder),
    );
  }, [busyModeLogData, sortOrder, sortBy, currentPage, currentSize]);

  useEffect(() => {
    const indexOfLastPerm = currentPage * currentSize;
    const indexOfFirstPerm = indexOfLastPerm - currentSize;
    setPaginatedTableRowData(
      tableRowData.slice(indexOfFirstPerm, indexOfLastPerm),
    );
  }, [tableRowData, currentPage, currentSize, sortBy, sortOrder]);

  // HANDLES
  const handleSorting = newSortBy => {
    const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newSortOrder);
    setSortBy(newSortBy);
    setCurrentPage(DEFAULT_PAGE_NO);
  };
  const getSortClass = field => {
    let sortClass = 'sc-sort';
    if (sortOrder === 'asc' && sortBy === field) {
      sortClass = 'sc-keyboard-arrow-up';
    } else if (sortOrder === 'desc' && sortBy === field) {
      sortClass = 'sc-keyboard-arrow-down';
    }
    return `cursor-pointer ${sortClass}`;
  };
  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setCurrentSize(size);
  };

  const handleCSVDownload = () => {
    const CSVData = generatePauseStoreLogCSVData(
      headerKeys,
      tableRowData,
      selectedBranch,
      brands,
    );

    downloadCsvFromJSON(
      CSVData,
      `paused-store-log-${format(new Date(), 'yyyy-mm-dd hh:mm')}`,
    );
  };

  const handleXLSXDownload = () => {
    const CSVData = generatePauseStoreLogCSVData(
      headerKeys,
      tableRowData,
      selectedBranch,
      brands,
    );
    const xlsxData = generateXLSXDataFromCSVData(CSVData);

    downloadXLSXFromJSON(
      xlsxData,
      `paused-store-log-${format(new Date(), 'yyyy-mm-dd hh:mm')}`,
    );
  };

  // RENDER

  // actionButton
  const renderDownloadCSVButton = !isEmpty(tableRowData) ? (
    <div className="flex items-center gap-x-2">
      <SCButton
        variant="secondary"
        size="sm"
        className="rounded-lg bg-grey-light text-black hover:bg-grey gap-x-1 w-auto flex items-center px-4 h-6 text-xs font-medium"
        action={handleCSVDownload}
      >
        <i className="kt-download" />
        <p>CSV</p>
      </SCButton>
      <SCButton
        variant="secondary"
        size="sm"
        className="rounded-lg bg-grey-light text-black hover:bg-grey gap-x-1 w-auto flex items-center px-4 h-6 text-xs font-medium"
        action={handleXLSXDownload}
      >
        <i className="kt-download" />
        <p>XLSX</p>
      </SCButton>
    </div>
  ) : null;

  // pagination
  const pagination =
    !busyModeLogDataLoading && tableRowData && tableRowData.length ? (
      <SCPagination
        className="mt-4"
        total={
          tableRowData && tableRowData.length > 0 ? tableRowData.length : 0
        }
        page={currentPage}
        size={currentSize}
        sizes={DEFAULT_PAGE_SIZES}
        onPageChange={handlePageChange}
      />
    ) : null;

  const placeholderStyle = { minHeight: 280 };

  // loaders
  const renderLoader = (
    <div
      className="flex w-full items-center justify-center"
      style={placeholderStyle}
    >
      <SCLoader lineWidth={5} diameter={50} />
    </div>
  );

  const renderNoData = (
    <div
      className="flex w-full items-center justify-center text-2xl text-grey-darker"
      style={placeholderStyle}
    >
      No data found
    </div>
  );

  // tableHeader
  const renderTableHeaderEl = headerKeys.map((cell, index) => (
    <th
      key={`discount-header-cell-${index + 1}`}
      className="border-b border-grey p-3 text-xs font-semibold uppercase text-grey-darker"
    >
      <div className="flex gap-x-2 justify-between items-center">
        <div className="flex">
          {cell.title}
          {cell.key === 'busy_mins' ? (
            <HelpPopover
              isPopoverOpen={isHeaderPopoverOpen}
              position="top"
              isHtmlElement
              content="No of busy minutes of the pause store"
              setIsPopoverOpen={setIsHeaderPopoverOpen}
              iconClassNames="sc-information cursor-pointer ml-1"
            />
          ) : null}
        </div>
        {cell.sortable ? (
          <button
            type="button"
            className={getSortClass(cell.key)}
            onClick={() => {
              handleSorting(cell.key);
            }}
          >
            {/* button content */}
          </button>
        ) : null}
      </div>
    </th>
  ));

  // tableBody
  const tableRowsEl = !isEmpty(paginatedTableRowData) ? (
    paginatedTableRowData.map(cell => {
      const {
        start_time: startTime,
        end_time: endTime,
        branch_id: branchId,
        busy_mins: busyMins,
        brand_id: brandId,
        busy_mode_reason: busyModeReason,
      } = cell;

      const matchingBranch = selectedBranch.find(
        branch => branch.id === branchId,
      );
      const branchTitle = matchingBranch ? matchingBranch.title : '';

      const matchingBrand = brands.find(brand => brand.id === brandId);

      const brandTitle = matchingBrand ? matchingBrand.title : '';

      return (
        <tr key={`busy-mode-row-${brandId * branchId + startTime + endTime}`}>
          <td className="px-3 py-3 border-b border-grey text-xs">
            {formatDateExact(startTime)}
          </td>
          <td className="px-3 py-3 border-b border-grey text-xs">
            {formatDateExact(endTime)}
          </td>
          <td className="px-3 py-3 border-b border-grey">{brandTitle}</td>
          <td className="px-3 py-3 border-b border-grey">{branchTitle}</td>
          <td className="px-3 py-3 border-b border-grey">
            {convertToHoursAndMinutes(busyMins) || '0m'}
          </td>
          <td className="p-4 border-b">{busyModeReason || 'N/A'}</td>
        </tr>
      );
    })
  ) : (
    <tr className="text-center">
      <td colSpan="11" className="p-5 text-sm text-grey-dark">
        No data found
      </td>
    </tr>
  );

  // table
  const renderTable = (
    <div>
      <div className="h-64 max-h-full overflow-y-auto mb-4">
        <table className="table-auto w-full">
          <thead>
            <tr>{renderTableHeaderEl}</tr>
          </thead>
          <tbody>{tableRowsEl}</tbody>
        </table>
      </div>

      {tableRowData && tableRowData.length ? pagination : null}
    </div>
  );

  const renderBodyContent = () => {
    if (busyModeLogDataLoading) return renderLoader;
    if (isEmpty(tableRowData)) return renderNoData;
    return renderTable;
  };

  return (
    <SectionCard
      title="Pause Log"
      metricKey="busyMode"
      actions={renderDownloadCSVButton}
    >
      {renderBodyContent()}
    </SectionCard>
  );
};

BusyModeLogTable.propTypes = {
  busyModeLogData: PropTypes.any,
  busyModeLogDataLoading: PropTypes.bool,
  selectedBranch: PropTypes.any,
  brands: PropTypes.array,
};
const mapStateToProps = createStructuredSelector({
  busyModeLogData: selectors.makeSelectBusyModeLogData(),
  busyModeLogDataLoading: selectors.makeSelectBusyModeLogDataLoading(),
  selectedBranch: selectors.makeSelectAnalyticsSelectedBranch(),
  brands: selectors.makeSelectProperty('brands'),
});

const withConnect = connect(mapStateToProps);

export default compose(withConnect)(BusyModeLogTable);
